import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import React, { useState } from 'react';
import {
    Bar, BarChart, CartesianGrid,
    Legend, PolarAngleAxis, PolarGrid, PolarRadiusAxis,
    Radar, RadarChart, ResponsiveContainer,
    Tooltip,
    XAxis, YAxis,
} from 'recharts';
import { FadedPaper } from '../../theme/StyledFields';
import KeyTakeaway from './KeyTakeaway';
import VizToggle, { VizType } from './VizToggle';
import { DomainRankingRow, ReportSection } from './types';

const COLORS = ['#8884d8', '#82ca9d'];

interface Props {
    data: ReportSection<DomainRankingRow>;
}

const DomainRankingsSection: React.FC<Props> = ({ data }) => {
    const [viz, setViz] = useState<VizType>('radar');

    const radarData = data.rows.map((r) => ({
        domain: r.domain_name,
        median: r.median_score,
        avg: r.avg_score,
    }));

    const barData = [...data.rows]
        .sort((a, b) => a.median_score - b.median_score)
        .map((r) => ({
            domain: r.domain_name,
            median: r.median_score,
            avg: r.avg_score,
        }));

    return (
        <FadedPaper sx={{ p: 3, mb: 3 }} data-testid="section-domain-rankings">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h5">Team Skill Profile</Typography>
                <VizToggle value={viz} options={['radar', 'bar', 'table']} onChange={setViz} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Domains ranked from growth opportunities to most confident, by median score across all submissions.
            </Typography>

            {viz === 'radar' && (
                <Box sx={{ width: '100%', maxWidth: 500, mx: 'auto' }}>
                    <ResponsiveContainer width="100%" height={350}>
                        <RadarChart data={radarData}>
                            <PolarGrid />
                            <PolarAngleAxis dataKey="domain" tick={{ fontSize: 11 }} />
                            <PolarRadiusAxis angle={90} domain={[0, 5]} tick={{ fontSize: 10 }} />
                            <Radar name="Median" dataKey="median" stroke={COLORS[0]} fill={COLORS[0]} fillOpacity={0.3} strokeWidth={2} />
                            <Radar name="Average" dataKey="avg" stroke={COLORS[1]} fill={COLORS[1]} fillOpacity={0.15} strokeWidth={2} />
                            <Legend />
                        </RadarChart>
                    </ResponsiveContainer>
                </Box>
            )}

            {viz === 'bar' && (
                <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={barData} layout="vertical" margin={{ left: 120 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" domain={[0, 5]} />
                        <YAxis type="category" dataKey="domain" tick={{ fontSize: 12 }} width={110} />
                        <Tooltip />
                        <Bar dataKey="median" name="Median" fill={COLORS[0]} radius={[0, 4, 4, 0]} />
                        <Bar dataKey="avg" name="Average" fill={COLORS[1]} radius={[0, 4, 4, 0]} />
                        <Legend />
                    </BarChart>
                </ResponsiveContainer>
            )}

            {viz === 'table' && (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Rank</TableCell>
                                <TableCell>Domain</TableCell>
                                <TableCell align="right">Median</TableCell>
                                <TableCell align="right">Avg</TableCell>
                                <TableCell align="right">Diff from Org Median</TableCell>
                                <TableCell align="right">n</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.rows.map((r) => (
                                <TableRow key={r.domain_name}>
                                    <TableCell>{r.weakness_rank}</TableCell>
                                    <TableCell>{r.domain_name}</TableCell>
                                    <TableCell align="right">{r.median_score}</TableCell>
                                    <TableCell align="right">{r.avg_score}</TableCell>
                                    <TableCell align="right" sx={{ color: r.diff_from_org_median < 0 ? 'error.main' : 'success.main' }}>
                                        {r.diff_from_org_median > 0 ? '+' : ''}{r.diff_from_org_median}
                                    </TableCell>
                                    <TableCell align="right">{r.submission_count}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <KeyTakeaway text={data.key_takeaway} />
        </FadedPaper>
    );
};

export default DomainRankingsSection;
